import { Button, Checkbox, Group, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PriceListItem } from '@/api/generated/models';
import { useListItems } from '@/api/generated/price-lists/price-lists';
import { useListUnits } from '@/api/generated/units/units';
import { FormModal, ModalFooter, SelectField, TextField } from '@/components/common';
import { findUnitLabel } from '@/features/units/unitDisplay';
import { formatAmount } from '@/utils/format';

interface ContractItemPickerModalProps {
  opened: boolean;
  onClose: () => void;
  priceListId?: string;
  /** Stavke cjenika koje su već na ugovoru (spremljene ili dodane u nacrt). */
  existingPriceListItemIds: string[];
  /** Odabrane stavke idu u nacrt (oznaka NOVO) — spremaju se tek s "Spremi stavke". */
  onAdd: (items: PriceListItem[]) => void;
}

export const ContractItemPickerModal = ({
  opened,
  onClose,
  priceListId,
  existingPriceListItemIds,
  onAdd
}: ContractItemPickerModalProps) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data } = useListItems(priceListId ?? '', { query: { enabled: opened && !!priceListId } });
  const { data: unitsData } = useListUnits();
  const alreadyAdded = new Set(existingPriceListItemIds);

  const pickableItems: PriceListItem[] = (data ?? []).filter(
    (item) => item.isActive && !(item.id && alreadyAdded.has(item.id))
  );
  const categories = Array.from(
    new Set(pickableItems.map((item) => item.category).filter(Boolean))
  ) as string[];

  const query = search.trim().toLowerCase();
  const items: PriceListItem[] = pickableItems.filter((item) => {
    if (categoryFilter && item.category !== categoryFilter) return false;
    if (!query) return true;
    return (
      item.itemCode?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query)
    );
  });

  const selectableIds = items.map((item) => item.id).filter((id): id is string => !!id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id));

  const toggle = (id?: string) => {
    if (!id) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const reset = () => {
    setSearch('');
    setCategoryFilter(null);
    setSelected(new Set());
  };

  const handleAdd = () => {
    onAdd(pickableItems.filter((item) => !!item.id && selected.has(item.id)));
    reset();
    onClose();
  };

  return (
    <FormModal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      size={760}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('contractItemPicker.title')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        <Group gap={12} wrap="wrap" align="flex-end">
          <div style={{ flex: 1, minWidth: 220 }}>
            <TextField
              label={t('contractItemPicker.searchLabel')}
              placeholder={t('contractItemPicker.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
          </div>
          <div style={{ width: 200 }}>
            <SelectField
              hideLabel
              placeholder={t('contractItemPicker.allCategories')}
              clearable
              data={[
                { value: '', label: t('contractItemPicker.allCategories') },
                ...categories.map((c) => ({ value: c, label: c }))
              ]}
              value={categoryFilter ?? ''}
              onChange={(value) => setCategoryFilter(value || null)}
            />
          </div>
        </Group>

        <Stack gap={0} style={{ maxHeight: 320, overflowY: 'auto' }}>
          <Group
            gap={8}
            wrap="nowrap"
            py={4}
            style={{ borderBottom: '1px solid var(--color-border-soft)' }}
          >
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              disabled={selectableIds.length === 0}
              onChange={toggleAll}
              aria-label={t('contractItemPicker.selectAll')}
            />
            <Text fz={11} fw={600} w={90} style={{ color: 'var(--color-text-muted)' }}>
              {t('contractItemPicker.colCode')}
            </Text>
            <Text fz={11} fw={600} style={{ flex: 1, color: 'var(--color-text-muted)' }}>
              {t('contractItemPicker.colName')}
            </Text>
            <Text fz={11} fw={600} w={110} style={{ color: 'var(--color-text-muted)' }}>
              {t('contractItemPicker.colCategory')}
            </Text>
            <Text fz={11} fw={600} w={70} style={{ color: 'var(--color-text-muted)' }}>
              {t('contractItemPicker.colUnit')}
            </Text>
            <Text fz={11} fw={600} w={90} ta="right" style={{ color: 'var(--color-text-muted)' }}>
              {t('contractItemPicker.colPrice')}
            </Text>
          </Group>

          {items.length === 0 && (
            <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
              {t('contractItemPicker.emptyState')}
            </Text>
          )}

          {items.map((item, index) => (
            <Group
              key={item.id}
              gap={8}
              wrap="nowrap"
              py={10}
              role="button"
              tabIndex={0}
              onClick={() => toggle(item.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') toggle(item.id);
              }}
              style={{
                cursor: 'pointer',
                borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
              }}
            >
              <Checkbox
                checked={!!item.id && selected.has(item.id)}
                onChange={() => toggle(item.id)}
                onClick={(event) => event.stopPropagation()}
              />
              <Text fz={13} fw={600} w={90} style={{ color: 'var(--color-text-primary)' }}>
                {item.itemCode}
              </Text>
              <Text fz={13} style={{ flex: 1, color: 'var(--color-text-secondary)' }} truncate>
                {item.description}
              </Text>
              <Text fz={12} w={110} style={{ color: 'var(--color-text-secondary)' }} truncate>
                {item.category ?? '—'}
              </Text>
              <Text fz={13} w={70} style={{ color: 'var(--color-text-muted)' }}>
                {findUnitLabel(unitsData, item.unitId, i18n.language)}
              </Text>
              <Text
                fz={13}
                fw={600}
                w={90}
                ta="right"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {formatAmount(item.unitPrice)}
              </Text>
            </Group>
          ))}
        </Stack>

        <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
          {t('itemsDraft.pickerNote')}
        </Text>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button disabled={selected.size === 0} onClick={handleAdd}>
            {t('contractItemPicker.addSelected', { count: selected.size })}
          </Button>
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
